/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { KEY_ENTER } from '@gh/core-ui';
import { coalesce } from '@gh/core-util';
import { Column, ICellRendererParams, RowNode } from 'ag-grid-community';
import { noop } from 'lodash';
import { EditableCellModel, EditableCellModelFactory } from '../../../state';
import { GridCellRendererComponent } from '../../grid-component.interfaces';

@Component({
  selector: 'gh-grid-checkbox-cell-renderer',
  template: require('./grid-checkbox-cell-renderer.component.html'),
  styles: [require('./grid-checkbox-cell-renderer.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridCheckboxCellRendererComponent implements GridCellRendererComponent, OnDestroy {
  checked: boolean;
  color: string;
  label?: string;

  private node: RowNode;
  private column: Column;
  private data: any;

  private modelFactory: EditableCellModelFactory;
  private model: EditableCellModel;
  private useEditableAsEnabled = false;

  private _onChange = noop;
  private eGridCell: HTMLElement;

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  get editable(): boolean {
    return this.useEditableAsEnabled ? true : this.model.editable;
  }

  get enabled(): boolean {
    return this.useEditableAsEnabled ? this.model.editable : true;
  }

  agInit(params: ICellRendererParams): void {
    this.node = params.node;
    this.column = params.column;

    this.modelFactory = params['modelFactory'];

    this.color = params['color'] || 'primary';
    this.useEditableAsEnabled = coalesce(params['useEditableAsEnabled'], false);
    this.label = params['label'];
    this._onChange = params['onChange'];
    this.eGridCell = params.eGridCell;

    this.eGridCell.addEventListener('keydown', this.onKeyDown);

    this.refresh(params);
  }

  ngOnDestroy(): void {
    this.eGridCell.removeEventListener('keydown', this.onKeyDown);
  }

  refresh(params: any): boolean {
    const cellParams = {
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    };
    const model = this.model = this.modelFactory(cellParams);
    const value = model.value;

    this.data = params.data;

    if (this.checked !== value) {
      this.checked = value;
    }
    this.changeDetectorRef.markForCheck();
    return true;
  }

  onChange(checked: boolean): void {
    this._onChange({ node: this.node, colDef: this.column.getColDef(), data: this.data }, checked);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.which === KEY_ENTER) {
      this.onChange(!this.checked);
    }
  }
}
